// @givmo/board — the reads behind a copy of the return and its receipts (CBG-YR5-DIST's web half,
// CBG-YR5-REVIEW), and the answers the pages give in words.
//
// The routes are dark while `BOARD_RETURN_DISTRIBUTION_ENABLED` is off: every one is the route miss
// then, on both doors and every method (`board-return-review.md` §7). So a 404 is the backend's own
// answer that the copy isn't here to read, which the page says in one quiet line, never as a failure.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { useBoardOperations } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import { boardForm990Keys } from '../form990/form990-queries';
import { decodeChoices, decodeCopy, decodeRecipients, type ReturnCopy } from './return-decode';

/** The roster's grant act: providing, Email again, a printed copy, withdrawing (DIST §11.1). */
export const RETURN_ACT_PERMISSION = 'board_member:grant_access';
/** The in-meeting record's string: linking the board's own act as its review (REVIEW §8). */
export const REVIEW_LINK_PERMISSION = 'board_meeting:write';

/** The return's query keys, rooted per organization. */
export function boardReturnKeys(orgRef: string | null) {
  const root = ['board-return', orgRef ?? ''] as const;
  return {
    root,
    copy: (recordId: string) => [...root, 'copy', recordId] as const,
    recipients: [...root, 'recipients'] as const,
    candidates: [...root, 'review-candidates'] as const,
  };
}

/** A served refusal's own sentence, else the page's fallback. */
export function servedSentence(error: unknown, fallback: string): string {
  if (isGivmoError(error)) {
    const detail = error.entries[0]?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) return detail.trim();
  }
  return fallback;
}

export type ReturnCopyRead = { kind: 'copy'; copy: ReturnCopy } | { kind: 'not-served' };

/** One copy and its receipts (frame p7). */
export function useReturnCopy(orgRef: string | null, recordId: string, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardReturnKeys(orgRef).copy(recordId),
    enabled: enabled && orgRef !== null,
    retry: 1,
    queryFn: async (): Promise<ReturnCopyRead> => {
      try {
        const { data } = await operations.officerGetBoardReturnDistribution({
          path: { org_ref: orgRef ?? '', record_id: recordId },
          throwOnError: true,
        });
        const copy = decodeCopy(data);
        if (copy === null) throw new Error('The copy of the return could not be decoded.');
        return { kind: 'copy', copy };
      } catch (error) {
        if (isNotServed(error)) return { kind: 'not-served' };
        throw error;
      }
    },
  });
}

/** Who a copy would go to now (the providing dialog), directors first, as served. */
export function useReturnRecipients(orgRef: string | null, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardReturnKeys(orgRef).recipients,
    enabled: enabled && orgRef !== null,
    retry: 1,
    queryFn: async () => {
      const { data } = await operations.officerPreviewBoardReturnRecipients({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return decodeRecipients(data);
    },
  });
}

/** The board's own acts a review link may name (the picker), as served. */
export function useReviewCandidates(orgRef: string | null, enabled: boolean) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardReturnKeys(orgRef).candidates,
    enabled: enabled && orgRef !== null,
    retry: 1,
    queryFn: async () => {
      const { data } = await operations.officerListBoardReturnReviewCandidates({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return decodeChoices(data);
    },
  });
}

/**
 * After an act on a copy: the copy's own read takes the act's answer (each act answers the receipts
 * read), and the Form 990 page reads its lines 11a and 11b again.
 */
export function useSettleReturnAct(orgRef: string | null) {
  const queryClient = useQueryClient();
  return async (body: unknown) => {
    const copy = decodeCopy(body);
    if (copy !== null) {
      const next: ReturnCopyRead = { kind: 'copy', copy };
      queryClient.setQueryData(boardReturnKeys(orgRef).copy(copy.record_id), next);
    }
    await queryClient.invalidateQueries({ queryKey: boardReturnKeys(orgRef).root });
    await queryClient.invalidateQueries({ queryKey: boardForm990Keys(orgRef).root });
  };
}
